// NO DEPENDENCIES!

// The set of valid feature flag keys. This enum is the only part of a flag that
// lives in code — everything else (label, description, type, parent, requires,
// defaultValue, staffOverride, inDevelopment) is DB-controlled and staff-editable
// on the `FeatureFlagAnnotation` table.
//
// ⚠️ When you add a key here, ship a DB migration that seeds its
// `FeatureFlagAnnotation` row (see
// packages/encamp-prisma/prisma/migrations/*_move_feature_flag_metadata_to_db
// for the INSERT ... ON CONFLICT pattern). Nothing backfills rows automatically:
// a key with no row resolves to `false` everywhere (no default, no staff
// override) until the migration runs.
export enum FeatureFlagKey {
  RcraFacilityProfile = "rcra-facility-profile",
  Sse = "sse",

  TasksModule = "tasks-module",
  DocumentAI = "document-ai",
  SuggestedTasks = "suggested-tasks",
  TaskCalendarICSIntegration = "task-calendar-ics-integration",

  TierIIModule = "tierii-module", // formerly "module-tierii"
  Products = "products", // formerly "countable-items-ui"
  Measurements = "measurements", // formerly "eager-beaver"
  BypassChemicalVerification = "bypass-chemical-verification",
  FacilitySelfAssignment = "facility-self-assignment",
  LockOwnerOperator = "lock-owner-operator",
  InsightsUI = "insights-ui",
  Scout = "scout",
  ScoutRegulatoryAdvisorPermissionScoped = "scout-regulatory-advisor-permission-scoped",
  ScoutPro = "scout-pro",
  ScoutProPermissionScoped = "scout-pro-permission-scoped",
  ScoutReasoningText = "scout-reasoning-text",
  ScoutLoadTest = "scout-load-test",
  ScoutWriteAccess = "scout-write-access",
  ScoutSandbox = "scout-sandbox",

  WasteModule = "waste-module", // formerly "module-waste"
  WasteSubmissions = "waste-submissions", // formerly "submissions"
  WasteSubmissionStepper = "waste-submission-stepper",
  WasteDocumentSubmissionStepper = "waste-document-submission-stepper",
  WasteStateRegistration = "waste-state-registration",
  WasteEpaCorrectionDiscrepancies = "waste-epa-correction-discrepancies",
  WasteExceptionReporting = "waste-exception-reporting",
  WasteEpaDocumentIngest = "waste-epa-document-ingest",
  WasteGenerationCard = "waste-generation-card",
  WasteImplausibleSpecificGravity = "waste-implausible-specific-gravity",
  ContainerTracking = "container-tracking",
  ManifestInvoiceTracking = "manifest-invoice-tracking",
  ManifestViewsV2 = "manifest-views-v2",
  GeppettoSelfHealing = "geppetto-self-healing",
  Notifications = "notifications-module",
  HarmonizedViolations = "harmonized-violations",
  CersTracker = "cers-tracker",
  CersAiInfo = "cers-ai-info",
}

export enum FeatureFlagType {
  Module = "MODULE",
  AddOn = "ADD_ON",
  Other = "OTHER",
}

export type FeatureFlagMap = Record<FeatureFlagKey, boolean>;

/**
 * A flag's metadata, as stored in the `FeatureFlagAnnotation` table (the
 * source of truth — this used to be the `FEATURE_FLAG_META_BY_KEY` code
 * constant). The set of valid keys still lives in code ({@link FeatureFlagKey}),
 * but everything about a flag is now DB-controlled and staff-editable.
 *
 * Field notes:
 * - `type` — structural classification driving staff-UI grouping (MODULE →
 *   top-level product section, ADD_ON → nested under `parentKey`, OTHER →
 *   staff/dev toggles and experiments).
 * - `parentKey` — owning MODULE for an ADD_ON.
 * - `defaultValue` — value used when a tenant has no `TenantConfig` row. The
 *   "global default" in the evaluation chain (TenantConfig → default → false).
 * - `staffOverride` — staff users see the flag as on regardless of tenant
 *   config.
 * - `inDevelopment` — still under active development; surfaced as an "In
 *   development" chip and drives the preview affordance. Orthogonal to `type`.
 * - `requires` — other flags that must be on for this flag to be meaningful,
 *   beyond the implicit `parentKey` dependency. Display-only; not enforced.
 */
export type FeatureFlagMetadata = {
  label: string;
  description: string;
  type: FeatureFlagType;
  parentKey: FeatureFlagKey | null;
  defaultValue: boolean;
  staffOverride: boolean;
  inDevelopment: boolean;
  requires: readonly FeatureFlagKey[];
};

/**
 * All known flag keys. Derived from the {@link FeatureFlagKey} enum — the enum
 * is the in-code registry of valid keys; everything else about a flag lives in
 * the DB (`FeatureFlagAnnotation`).
 */
export const KNOWN_FEATURE_FLAG_KEYS = Object.values(FeatureFlagKey) as readonly FeatureFlagKey[];

export function isFeatureFlagKey(key: string): key is FeatureFlagKey {
  return KNOWN_FEATURE_FLAG_KEYS.includes(key as FeatureFlagKey);
}

/**
 * Build a resolved flag map from a tenant's stored `{key, value}` rows.
 *
 * Resolution per key: explicit tenant value → `defaultsByKey[key]` (the
 * DB-backed global default) → `false`. Callers pass the defaults map they read
 * from `FeatureFlagAnnotation`; omitting it makes every unset flag resolve to
 * `false` (used by tests that only care about the explicit rows).
 */
export function deriveFeatureFlagMapFromArray(
  keyValArray: { key: string; value: boolean }[],
  defaultsByKey?: Partial<Record<FeatureFlagKey, boolean>>
): FeatureFlagMap {
  const valByKey = new Map(keyValArray.map(({ key, value }) => [key, value]));
  const flagMap = KNOWN_FEATURE_FLAG_KEYS.reduce<Partial<FeatureFlagMap>>((acc, key) => {
    acc[key] = valByKey.get(key) ?? defaultsByKey?.[key] ?? false;
    return acc;
  }, {});
  // as-cast here only from Partial<FeatureFlagMap> to FeatureFlagMap
  // because we know all keys are present since we just iterated over them
  return flagMap as FeatureFlagMap;
}
